import moment from 'moment';
import { uniqBy, cloneDeep } from 'lodash';
import colors from 'theme/Colors.module.scss';

export const SCALE_FACTOR = 0.4;
export const FONT_SCALE_FACTOR = 0.675;
export const defaultGridSize = 15;
export const defaultGridColor = colors.lighterGrey;
export const bufferModes = ['fit', 'fill'];
export const zoomLevels = [
  { friendlyName: '300%', zoomVal: 3 },
  { friendlyName: '200%', zoomVal: 2 },
  { friendlyName: '125%', zoomVal: 1.25 },
  { friendlyName: '100%', zoomVal: 1 },
  { friendlyName: '75%', zoomVal: 0.75 },
  { friendlyName: '50%', zoomVal: 0.5 },
  { friendlyName: '25%', zoomVal: 0.25 },
  { friendlyName: '10%', zoomVal: 0.1 },
];

export const loadBGIntoCanvas = (img, conf, canvas) => {
  return new Promise(resolve => {
    const canvasCallback = canvas.renderAll.bind(canvas);
    const finalize = () => {
      canvas && canvasCallback();
      resolve();
    };

    canvas.setBackgroundImage(img, finalize, conf);
  });
};

export const downloadFonts = fonts => {
  return new Promise((resolve, reject) => {
    const promisesList = fonts.map(font =>
      new FontFace(font.fontFamily, font.fontURL).load().catch(err => err)
    );
    Promise.all(promisesList)
      .then(res => {
        res.forEach(uniqueFont => {
          if (uniqueFont && uniqueFont.family) {
            document.fonts.add(uniqueFont);
          }
        });
        resolve(true);
      })
      .catch(err => reject(err));
  });
};

export const getTemplateFonts = (args, userFonts) => {
  const cloneTemplate = cloneDeep(args.template); // Deep clone avoids frozen state
  const elements =
    cloneTemplate.ad_template_pages?.length > 0
      ? cloneTemplate.ad_template_pages.reduce((acc, x) => {
          return acc.concat(x.ad_template_vars);
        }, [])
      : cloneTemplate.ad_template_vars;
  const setMessage = args.setMessage;

  let fonts = [];
  const privateFonts = userFonts.private;
  const publicFonts = userFonts.public;
  Object.keys(publicFonts).forEach(key => {
    const items = publicFonts[key];
    fonts = fonts.concat(items);
  });
  fonts = fonts.concat(privateFonts);

  const fontsArray = elements
    // eslint-disable-next-line array-callback-return
    ?.map(item => {
      if (item.metadata) {
        const {
          res_type,
          metadata: {
            fontFamily,
            __fontFilename: fontFile,
            postscriptname,
            postScriptName,
          },
        } = item;
        if (!fontFamily) return undefined;
        if (res_type === 'txt' || res_type === 'txt_template') {
          const fontURL = fonts
            ?.filter(font => !!font.url)
            // eslint-disable-next-line array-callback-return
            .find(({ url, postscriptname: scriptFont, fullname }) => {
              const fileName = url
                .substring(url.lastIndexOf('/') + 1)
                .split('?')[0];

              if (postscriptname) return scriptFont === postscriptname;
              if (postScriptName) return scriptFont === postScriptName;
              if (fontFamily === fullname) return true;
              if (!postscriptname && fontFamily) return fileName === fontFile;
            })?.url;
          if (!fontURL) {
            const message = `Error loading: ${fontFamily}`;
            if (setMessage) {
              console.error('error: ', message);
            } else {
              console.error(message);
            }
            return undefined;
          }
          return { fontFamily, fontURL: `url(${fontURL})` };
        }
      }
    })
    .filter(i => !!i);
  return uniqBy(fontsArray, obj => obj.fontURL);
};

const getDuplicateTemplateName = name => {
  const originalName = name.replace(
    /\s?(\(duplicated at \d+\/\d+\/\d+ \d+:\d+:\d+ \w+\))$/i,
    ''
  );

  return `${originalName} (duplicated at ${moment().format(
    'M/D/YYYY h:mm:ss A'
  )})`;
};

export const getDuplicatePayload = async template => {
  const workingTemplate = {
    ...cloneDeep(template), // Deep clone avoids frozen state
    name: getDuplicateTemplateName(template.name),
  };

  // Clean up keys that server will assign
  workingTemplate.ad_template_vars.forEach(item => {
    delete item.id;
    delete item.ad_template_id;
  });

  delete workingTemplate.id;
  delete workingTemplate.campaigns_count;
  delete workingTemplate.archived_at;

  //
  const payload = workingTemplate;

  if (workingTemplate.bg_img) {
    const { original_url } = workingTemplate.bg_img;

    const relativeURL = () => {
      const relativePath = `/uploads${original_url.split('/uploads')[1]}`;

      if (relativePath.includes('?')) {
        const corePath = relativePath.split('?');
        return corePath[0];
      }

      return relativePath;
    };
    const pathSplit = relativeURL().split('/');
    const extSplit = pathSplit[pathSplit.length - 1].split('.');
    const fileName = extSplit[0];
    const extension = extSplit[extSplit.length - 1];

    const res = await fetch(original_url);
    const data = await res.blob();
    const file = new File([data], `${fileName}.${extension}`, {
      type: `image/${extension || 'jpeg'}`,
    });

    payload.bg_img = file;
  } else {
    delete payload.bg_img;
  }
  return payload;
};

export const duplicateTemplate = async (
  template,
  context,
  successCallback,
  failureCallback
) => {
  const workingTemplate = {
    ...cloneDeep(template), // Deep clone avoids frozen state
    name: getDuplicateTemplateName(template.name),
  };

  // Clean up keys that server will assign
  workingTemplate.ad_template_vars.forEach(item => {
    delete item.id;
    delete item.ad_template_id;
  });

  delete workingTemplate.id;
  delete workingTemplate.campaigns_count;
  delete workingTemplate.archived_at;

  const getFileFromUrl = async url => {
    if (!url) return null;
    const relativeURL = () => {
      const relativePath = `/uploads${url.split('/uploads')[1]}`;

      if (relativePath.includes('?')) {
        const corePath = relativePath.split('?');
        return corePath[0];
      }

      return relativePath;
    };
    const pathSplit = relativeURL().split('/');
    const extSplit = pathSplit[pathSplit.length - 1].split('.');
    const fileName = extSplit[0];
    const extension = extSplit[extSplit.length - 1];

    const res = await fetch(url, { mode: 'cors', cache: 'no-cache' });
    const data = await res.blob();
    return new File([data], `${fileName}.${extension}`, {
      type: `image/${extension || 'jpeg'}`,
    });
  };

  const createFile = async () => {
    const payload = cloneDeep(workingTemplate);

    const callback = res => {
      if (successCallback) successCallback();

      const id = res.id;

      if (context.navigate && id) {
        context.navigate(`/template-editor?id=${id}`);
      }
    };

    if (payload.double_sided) {
      for (let i = 0; i < payload.ad_template_pages.length; i++) {
        if (payload.ad_template_pages[i]?.bg_img) {
          payload.ad_template_pages[i].bg_img = await getFileFromUrl(
            payload.ad_template_pages[i].bg_img?.original_url || ''
          );
        } else {
          delete payload.ad_template_pages[i].bg_img;
        }
      }
      try {
        const cratedDoubleSidedTemplate = await context
          .createDoubleSidedTemplate(payload)
          .unwrap();
        callback(cratedDoubleSidedTemplate);
      } catch (error) {
        console.log('Fail to create double sided template', error);
      }
    } else {
      if (payload.bg_img) {
        const { original_url } = payload.bg_img;
        payload.bg_img = await getFileFromUrl(original_url);
      } else {
        delete payload.bg_img;
      }
      console.log('createTemplate old payload', payload);
      try {
        const cratedTemplate = await context.createTemplate(payload).unwrap();
        callback(cratedTemplate);
      } catch (error) {
        console.log('Fail to create template', error);
      }
    }
  };
  try {
    await createFile();
  } catch (e) {
    console.error(e);
    failureCallback();
  }
};

export const duplicateTemplateNew = async (template, context) => {
  const workingTemplate = {
    ...cloneDeep(template), // Deep clone avoids frozen state
    name: getDuplicateTemplateName(template.name),
  };

  // Clean up keys that server will assign
  workingTemplate.ad_template_vars.forEach(item => {
    delete item.id;
    delete item.ad_template_id;
  });

  delete workingTemplate.id;
  delete workingTemplate.campaigns_count;
  delete workingTemplate.archived_at;

  const getFileFromUrl = async url => {
    if (!url) return null;
    const relativeURL = () => {
      const relativePath = `/uploads${url.split('/uploads')[1]}`;
      return relativePath.includes('?')
        ? relativePath.split('?')[0]
        : relativePath;
    };

    const pathSplit = relativeURL().split('/');
    const extSplit = pathSplit[pathSplit.length - 1].split('.');
    const fileName = extSplit[0];
    const extension = extSplit[extSplit.length - 1];

    const res = await fetch(url, { mode: 'cors', cache: 'no-cache' });
    const data = await res.blob();
    return new File([data], `${fileName}.${extension}`, {
      type: `image/${extension || 'jpeg'}`,
    });
  };

  if (workingTemplate.double_sided) {
    for (let i = 0; i < workingTemplate.ad_template_pages.length; i++) {
      if (workingTemplate.ad_template_pages[i]?.bg_img) {
        workingTemplate.ad_template_pages[i].bg_img = await getFileFromUrl(
          workingTemplate.ad_template_pages[i].bg_img?.original_url || ''
        );
      } else {
        delete workingTemplate.ad_template_pages[i].bg_img;
      }
    }

    const doublePageFormData =
      convertDoublePageTemplateToFromData(workingTemplate);

    const res = await context
      .createDoubleSidedTemplate(doublePageFormData)
      .unwrap();

    if (context.navigate && res?.id) {
      context.navigate(`/template-editor?id=${res.id}`);
    }
    return res;
  } else {
    if (workingTemplate.bg_img) {
      const { original_url } = workingTemplate.bg_img;
      workingTemplate.bg_img = await getFileFromUrl(original_url);
    } else {
      delete workingTemplate.bg_img;
    }

    const singlePageFormData =
      convertSinglePageTemplateToFormData(workingTemplate);

    const res = await context.createTemplate(singlePageFormData).unwrap();

    if (context.navigate && res?.id) {
      context.navigate(`/template-editor?id=${res.id}`);
    }
    return res;
  }
};

export const convertSinglePageTemplateToFormData = template => {
  const cloneTemplate = cloneDeep(template);
  const formData = new FormData();

  if (!cloneTemplate.ad_template_vars) {
    cloneTemplate.ad_template_vars = [];
  }

  if (!cloneTemplate.ad_template_pages) {
    cloneTemplate.ad_template_pages = [];
  }

  for (let key in cloneTemplate) {
    if (
      key === 'ad_template_vars' ||
      key === 'metadata' ||
      key === 'ad_template_pages'
    ) {
      formData.append(`${key}`, JSON.stringify(cloneTemplate[key]));
    } else {
      formData.append(`${key}`, cloneTemplate[key]);
    }
  }

  // For debugging
  // for (let key of formData.entries()) {
  //   console.log(`${key[0]}: ${key[1]}`);
  // }

  return formData;
};

export const convertDoublePageTemplateToFromData = template => {
  const cloneTemplate = cloneDeep(template);
  const formData = new FormData();

  if (!cloneTemplate.ad_template_vars) {
    cloneTemplate.ad_template_vars = [];
  }

  if (!cloneTemplate.ad_template_pages) {
    cloneTemplate.ad_template_pages = [];
  }

  if (
    cloneTemplate.ad_template_pages[0]?.bg_img ||
    cloneTemplate.ad_template_pages[1]?.bg_img
  ) {
    for (let i = 0; i < cloneTemplate.ad_template_pages.length; i++) {
      if (cloneTemplate.ad_template_pages[i]?.bg_img) {
        formData.append(
          cloneTemplate.ad_template_pages[i].bg_img.name,
          cloneTemplate.ad_template_pages[i].bg_img
        );
        cloneTemplate.ad_template_pages[i].bg_img =
          cloneTemplate.ad_template_pages[i].bg_img.name;
      }
    }
    formData.append('data', JSON.stringify(cloneTemplate));

    // For debugging
    // for (let key of formData.entries()) {
    //   console.log(`${key[0]}: ${key[1]}`);
    // }

    return formData;
  } else if (cloneTemplate?.bg_img) {
    for (let key in cloneTemplate) {
      if (
        key === 'ad_template_vars' ||
        key === 'metadata' ||
        key === 'ad_template_pages'
      ) {
        formData.append(`${key}`, JSON.stringify(cloneTemplate[key]));
      } else {
        formData.append(`${key}`, cloneTemplate[key]);
      }
    }

    return formData;
  } else {
    return formData;
  }
};

export const calcFitRatio = args => {
  const { canvasDimensions, fitMode, previewMode } = args;
  const { width, height } = canvasDimensions;
  const headerSpace = previewMode ? 75 : 135;
  const panelSpace = previewMode ? 0 : 460;
  const buffer = previewMode ? 62 : 30;

  const areaHeight = window.innerHeight - buffer - headerSpace;
  const areaWidth = window.innerWidth - buffer - panelSpace;
  let newZoomRatio;

  // Fit
  if (fitMode === bufferModes[0]) {
    const greaterWidth = width > areaWidth;
    const greaterHeight = height > areaHeight;

    if (!greaterWidth && greaterHeight) {
      newZoomRatio = areaHeight / height;
    } else if (greaterWidth && !greaterHeight) {
      newZoomRatio = areaWidth / width;
    } else {
      const widthRatio = areaWidth / width;
      const heightRatio = areaHeight / height;
      const lesserRatio = widthRatio < heightRatio ? widthRatio : heightRatio;
      newZoomRatio = lesserRatio;
    }
  }

  // Fill
  if (fitMode === bufferModes[1]) {
    const lesserWidth = width < areaWidth;
    const lesserHeight = height < areaHeight;

    if (!lesserWidth && lesserHeight) {
      newZoomRatio = areaHeight / height;
    } else if (lesserWidth && !lesserHeight) {
      newZoomRatio = areaWidth / width;
    } else {
      const widthRatio = areaWidth / width;
      const heightRatio = areaHeight / height;
      const lesserRatio = widthRatio > heightRatio ? widthRatio : heightRatio;
      newZoomRatio = lesserRatio;
    }
  }

  const newFitObj = {
    friendlyName: `${parseInt(newZoomRatio * 100)}%`,
    zoomVal: newZoomRatio,
  };

  return newFitObj;
};
